import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Target, TrendingUp, TrendingDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Budgets = () => {
  const [budgets, setBudgets] = useState([]);
  const [filteredBudgets, setFilteredBudgets] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filters, setFilters] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    store_id: 'all'
  });
  const [formData, setFormData] = useState({
    store_id: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    budget_type: 'GASTOS',
    budgeted_amount: ''
  });

  useEffect(() => {
    fetchStores();
    fetchBudgets();
  }, []);

  useEffect(() => {
    // Filtrar presupuestos
    let filtered = budgets;
    
    if (filters.store_id !== 'all') {
      filtered = filtered.filter(b => b.store_id === filters.store_id);
    }
    
    if (filters.year) {
      filtered = filtered.filter(b => b.year === filters.year);
    }
    
    if (filters.month) {
      filtered = filtered.filter(b => b.month === filters.month);
    }
    
    setFilteredBudgets(filtered);
  }, [budgets, filters]);

  const fetchStores = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/stores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStores(response.data);
    } catch (error) {
      console.error('Error al cargar tiendas');
    }
  };

  const fetchBudgets = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/budgets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBudgets(response.data);
    } catch (error) {
      toast.error('Error al cargar presupuestos');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      const dataToSend = {
        ...formData,
        budgeted_amount: parseFloat(formData.budgeted_amount)
      };
      
      await axios.post(`${API}/budgets`, dataToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Presupuesto creado correctamente');
      fetchBudgets();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear presupuesto');
    }
  };

  const resetForm = () => {
    setFormData({
      store_id: '',
      month: new Date().getMonth() + 1,
      year: new Date().getFullYear(),
      budget_type: 'GASTOS',
      budgeted_amount: ''
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-GT', {
      style: 'currency',
      currency: 'GTQ',
      minimumFractionDigits: 2
    }).format(value);
  };

  const getStoreName = (storeId) => {
    const store = stores.find(s => s.id === storeId);
    return store?.name || 'N/A';
  };

  const getMonthName = (month) => {
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return months[month - 1];
  };

  const getVariance = (budgeted, actual) => {
    return budgeted - actual;
  };

  const getVariancePercent = (budgeted, actual) => {
    if (budgeted === 0) return 0;
    return ((actual - budgeted) / budgeted) * 100;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="budgets-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Presupuestos</h1>
          <p className="text-slate-600 mt-1">Control de presupuestos por tienda</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="btn-primary" data-testid="add-budget-button">
              <Plus className="h-4 w-4 mr-2" />
              Nuevo Presupuesto
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nuevo Presupuesto</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="budget-form">
              <div className="space-y-2">
                <Label htmlFor="store_id">Tienda *</Label>
                <Select key="form-store-select" value={formData.store_id} onValueChange={(value) => setFormData({ ...formData, store_id: value })}>
                  <SelectTrigger data-testid="store-select">
                    <SelectValue placeholder="Seleccionar tienda" />
                  </SelectTrigger>
                  <SelectContent position="popper" sideOffset={4}>
                    {stores.filter(s => s.is_active).map(store => (
                      <SelectItem key={`form-store-${store.id}`} value={store.id}>{store.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="month">Mes *</Label>
                  <Select value={String(formData.month)} onValueChange={(value) => setFormData({ ...formData, month: parseInt(value) })} required>
                    <SelectTrigger data-testid="month-select">
                      <SelectValue placeholder="Seleccionar mes" />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                        <SelectItem key={month} value={String(month)}>{getMonthName(month)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="year">Año *</Label>
                  <Input
                    id="year"
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                    required
                    data-testid="year-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="budget_type">Tipo *</Label>
                <Select value={formData.budget_type} onValueChange={(value) => setFormData({ ...formData, budget_type: value })} required>
                  <SelectTrigger data-testid="type-select">
                    <SelectValue placeholder="Tipo de presupuesto" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GASTOS">Gastos</SelectItem>
                    <SelectItem value="COMPRAS">Compras</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="budgeted_amount">Monto Presupuestado (Q) *</Label>
                <Input
                  id="budgeted_amount"
                  type="number"
                  step="0.01"
                  value={formData.budgeted_amount}
                  onChange={(e) => setFormData({ ...formData, budgeted_amount: e.target.value })}
                  placeholder="0.00"
                  required
                  data-testid="amount-input"
                />
              </div>

              <Button type="submit" className="w-full btn-primary" data-testid="save-budget-button">
                Crear Presupuesto
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Tienda</Label>
              <Select key="filter-store" value={filters.store_id} onValueChange={(value) => setFilters({ ...filters, store_id: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas las tiendas" />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4}>
                  <SelectItem value="all">Todas las tiendas</SelectItem>
                  {stores.filter(s => s.is_active).map(store => (
                    <SelectItem key={`filter-store-${store.id}`} value={store.id}>{store.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Mes</Label>
              <Select key="filter-month" value={String(filters.month)} onValueChange={(value) => setFilters({ ...filters, month: parseInt(value) })}>
                <SelectTrigger>
                  <SelectValue placeholder="Mes" />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                    <SelectItem key={`filter-month-${month}`} value={String(month)}>{getMonthName(month)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Año</Label>
              <Select key="filter-year" value={String(filters.year)} onValueChange={(value) => setFilters({ ...filters, year: parseInt(value) })}>
                <SelectTrigger>
                  <SelectValue placeholder="Año" />
                </SelectTrigger>
                <SelectContent position="popper" sideOffset={4}>
                  <SelectItem key="filter-year-2024" value="2024">2024</SelectItem>
                  <SelectItem key="filter-year-2025" value="2025">2025</SelectItem>
                  <SelectItem key="filter-year-2026" value="2026">2026</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Budgets Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredBudgets.map((budget) => {
          const variance = getVariance(budget.budgeted_amount, budget.actual_amount);
          const variancePercent = getVariancePercent(budget.budgeted_amount, budget.actual_amount);
          const isOverBudget = variance < 0;

          return (
            <Card key={budget.id} className="hover-lift" data-testid={`budget-card-${budget.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold text-slate-900">
                    {getStoreName(budget.store_id)}
                  </CardTitle>
                  <Target className={`h-5 w-5 ${
                    isOverBudget ? 'text-red-600' : 'text-green-600'
                  }`} />
                </div>
                <p className="text-sm text-slate-600">
                  {getMonthName(budget.month)} {budget.year} - {budget.budget_type}
                </p>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Presupuestado:</span>
                    <span className="font-bold text-blue-700 font-mono">
                      {formatCurrency(budget.budgeted_amount)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Ejecutado:</span>
                    <span className="font-bold text-slate-900 font-mono">
                      {formatCurrency(budget.actual_amount)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                    <span className="text-sm font-semibold text-slate-700">Variación:</span>
                    <div className="flex items-center space-x-2">
                      {isOverBudget ? (
                        <TrendingDown className="h-4 w-4 text-red-600" />
                      ) : (
                        <TrendingUp className="h-4 w-4 text-green-600" />
                      )}
                      <span className={`font-bold font-mono ${
                        isOverBudget ? 'text-red-700' : 'text-green-700'
                      }`}>
                        {formatCurrency(Math.abs(variance))}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="space-y-1">
                  <div className="flex justify-between text-xs text-slate-600">
                    <span>Progreso</span>
                    <span className={isOverBudget ? 'text-red-600 font-semibold' : ''}>
                      {variancePercent.toFixed(1)}%
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        isOverBudget ? 'bg-red-600' : 'bg-green-600'
                      }`}
                      style={{
                        width: `${Math.min((budget.actual_amount / budget.budgeted_amount) * 100, 100)}%`
                      }}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {budgets.length === 0 && (
        <Card className="p-12 text-center">
          <Target className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No hay presupuestos registrados</h3>
          <p className="text-slate-600 mb-4">Comienza creando un presupuesto para tus tiendas</p>
          <Button onClick={() => setDialogOpen(true)} className="btn-primary">
            <Plus className="h-4 w-4 mr-2" />
            Crear Primer Presupuesto
          </Button>
        </Card>
      )}
    </div>
  );
};

export default Budgets;
